import { REPORT_GAIN_TYPE_COLUMN_LABEL } from "../../../utils/reports/reportDisplayLabels";
import { STOCK_TAX_REPORT_COLUMNS } from "../../../utils/equity/stockTaxReport";

export type FieldColumn = {
  key: string;
  value: string;
  locked?: boolean;
  minWidth?: number;
  align?: "left" | "center" | "right";
};

export const REIT_TABLE_SEGMENT = "REIT";

export const REIT_POINT_TO_POINT_REPORT = "Point to Point Return";
export const REIT_TAX_REPORT = "Tax Report";

export const REIT_REPORT_TYPE_OPTIONS = [
  { value: "Holding", label: "Unrealized" },
  { value: "Realized", label: "Realized" },
  { value: "All", label: "All" },
  { value: "Transaction", label: "Transaction" },
  { value: "MIS Report", label: "MIS Report" },
  { value: REIT_TAX_REPORT, label: REIT_TAX_REPORT },
  { value: REIT_POINT_TO_POINT_REPORT, label: REIT_POINT_TO_POINT_REPORT },
] as const;

export const REIT_QUICK_REPORTS = [
  { id: "holding", label: "Unrealized", reportType: "Holding" },
  { id: "realized", label: "Realized", reportType: "Realized" },
  { id: "all", label: "All", reportType: "All" },
  { id: "transaction", label: "Transaction Report", reportType: "Transaction" },
  { id: "mis", label: "MIS Report", reportType: "MIS Report" },
  { id: "tax", label: REIT_TAX_REPORT, reportType: REIT_TAX_REPORT },
  { id: "p2p", label: REIT_POINT_TO_POINT_REPORT, reportType: REIT_POINT_TO_POINT_REPORT },
];

export const INVESTOR_COLUMN: FieldColumn = {
  key: "investorDisplay",
  value: "Investor Name",
  locked: true,
  minWidth: 200,
};

/** aumsampaan REIT Holding columns (Unrealized) */
export const REIT_UNREALIZED_HOLDING_COLUMNS: FieldColumn[] = [
  { key: "symbol", value: "Scheme Name", locked: true, minWidth: 200 },
  { key: "isin", value: "ISIN", minWidth: 96 },
  { key: "units", value: "Qty", minWidth: 100 },
  { key: "averagePrice", value: "Avg. Cost", minWidth: 112 },
  { key: "currentPrice", value: "Current Price", minWidth: 112 },
  { key: "amount", value: "Invested", minWidth: 128 },
  { key: "currentAmount", value: "Current Value", minWidth: 128 },
  { key: "gainLoss", value: "P&L", minWidth: 132 },
  { key: "gainLossPerc", value: "P&L %", minWidth: 96 },
];

export const REIT_REALIZED_COLUMNS: FieldColumn[] = [
  { key: "schemeName", value: "Scheme Name", locked: true, minWidth: 200 },
  { key: "isin", value: "ISIN", minWidth: 96 },
  { key: "type", value: REPORT_GAIN_TYPE_COLUMN_LABEL, locked: true, minWidth: 132 },
  { key: "transactionDate", value: "Date", minWidth: 112 },
  { key: "units", value: "Units Sold", minWidth: 100 },
  { key: "purchaseNAV", value: "Sell Price", minWidth: 112 },
  { key: "avgBuyPrice", value: "Avg Buy Price", minWidth: 112 },
  { key: "saleValue", value: "Sale Value", minWidth: 128 },
  { key: "realisedPnl", value: "Realized P&L", minWidth: 132 },
  { key: "realisedPNLPercentage", value: "Realized P&L%", minWidth: 120 },
];

export const REIT_TRANSACTION_COLUMNS: FieldColumn[] = [
  { key: "transType", value: "Type", locked: true, minWidth: 120 },
  { key: "transactionDate", value: "Date", minWidth: 112 },
  { key: "units", value: "Qty", minWidth: 100 },
  { key: "purchasePrice", value: "Buy/Sell Price", minWidth: 112 },
  { key: "amount", value: "Amount", minWidth: 128 },
  { key: "currentAmount", value: "Current Value", minWidth: 128 },
];

export const REIT_ALL_COMMON_COLUMNS: FieldColumn[] = [
  { key: "schemeName", value: "Scheme Name", locked: true, minWidth: 200 },
  { key: "isin", value: "ISIN", minWidth: 96 },
  { key: "type", value: REPORT_GAIN_TYPE_COLUMN_LABEL, locked: true, minWidth: 132 },
  { key: "units", value: "Units", minWidth: 100 },
  { key: "costValue", value: "Invested Amount", minWidth: 128 },
  { key: "currentValue", value: "Current Value", minWidth: 128 },
  { key: "gainLoss", value: "P&L", minWidth: 132 },
  { key: "gainLossPercentage", value: "P&L %", minWidth: 96 },
];

export const REIT_MIS_COLUMNS: FieldColumn[] = [
  { key: "dateOfInvestment", value: "Date of Investment", locked: true, minWidth: 140 },
  { key: "symbol", value: "Stock Name", locked: true, minWidth: 200 },
  { key: "marketCap", value: "Market Cap", minWidth: 120 },
  { key: "investedAmount", value: "Purchase Value (₹)", minWidth: 140 },
  { key: "currentValue", value: "Current Value (₹)", minWidth: 140 },
  { key: "absoluteReturn", value: "AR (₹)", minWidth: 120 },
  { key: "absoluteReturnPerc", value: "AR (%)", minWidth: 96 },
];

export const REIT_P2P_COLUMNS: FieldColumn[] = [
  { key: "symbol", value: "Symbol", locked: true, minWidth: 112 },
  { key: "exchange", value: "Exchange", minWidth: 88 },
  { key: "transType", value: "Type", minWidth: 96 },
  { key: "units", value: "Units", minWidth: 112 },
  { key: "investedAmount", value: "Invested / Selling Amt.", minWidth: 152 },
  { key: "currentAmount", value: "Current Amount (₹)", minWidth: 152 },
  { key: "currentNAV", value: "Price", minWidth: 128 },
  { key: "avgPrice", value: "Avg Price", minWidth: 112 },
  { key: "xirr", value: "XIRR", minWidth: 96 },
  { key: "cagr", value: "CAGR", minWidth: 96 },
  { key: "unrealisedPNL", value: "Unrealised P&L", minWidth: 132 },
  { key: "unrealisedPNLPercentage", value: "Unrealised P&L%", minWidth: 120 },
  { key: "realisedPnl", value: "Realised P&L", minWidth: 132 },
  { key: "realisedPNLPercentage", value: "Realised P&L%", minWidth: 120 },
];

export const REIT_TAX_COLUMNS: FieldColumn[] = [
  ...STOCK_TAX_REPORT_COLUMNS.map((c) => ({
    key: c.key,
    value: c.value,
    locked: c.locked,
  })),
  { key: "Type", value: "Type", minWidth: 160 },
];

export const getColumnsForReitReportType = (reportType: string): FieldColumn[] => {
  switch (reportType) {
    case "Holding":
      return REIT_UNREALIZED_HOLDING_COLUMNS;
    case "Realized":
      return REIT_REALIZED_COLUMNS;
    case "All":
      return REIT_ALL_COMMON_COLUMNS;
    case "Transaction":
      return REIT_TRANSACTION_COLUMNS;
    case "MIS Report":
      return REIT_MIS_COLUMNS;
    case REIT_TAX_REPORT:
      return REIT_TAX_COLUMNS;
    case REIT_POINT_TO_POINT_REPORT:
      return REIT_P2P_COLUMNS;
    default:
      return REIT_UNREALIZED_HOLDING_COLUMNS;
  }
};

/** Holding and Point to Point need a date range (same as aumsampaan). */
export const reportTypeNeedsDateRange = (reportType: string): boolean =>
  reportType === "Holding" || reportType === REIT_POINT_TO_POINT_REPORT;
